import { Component, EventEmitter, Inject, OnInit, Output } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { SnackbarCustomService } from '../../../shared/components/snackbar/snackbar-custom.service';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatCardModule } from '@angular/material/card';
import { MatIcon } from '@angular/material/icon';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatInput } from '@angular/material/input';
import { MatDividerModule } from '@angular/material/divider';
import { ViaCepService } from '../../../core/services/address/via-cep/via-cep.service';
import { EmployeeStatusService } from '../../../core/services/employee/employee-status/employee-status.service';
import { EmployeeService } from '../../../core/services/employee/employee/employee.service';
import { AddressTypeService } from '../../../core/services/address/address-type/address-type.service';
import { EmployeeRoleService } from '../../../core/services/employee/employee-role/employee-role.service';
import { LocationService } from '../../../core/services/location/location/location.service';
import {
  ErrorRequestModel,
  ResponseRequestModel,
} from '../../../core/models/response.model';
import { CommonModule } from '@angular/common'; // Importe o CommonModule
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators'
import { EstadoCidade, estadosCidades } from '../../../core/models/addressIBGE'


@Component({
  selector: 'location-edit',
  imports: [
    ReactiveFormsModule,
    MatButton,
    MatProgressSpinner,
    MatExpansionModule,
    MatFormFieldModule,
    MatCardModule,
    MatIcon,
    MatInput,
    MatDividerModule,
    CommonModule, // Adicionado para o pipe 'async'
    MatAutocompleteModule, // Adicionado para o autocomplete
  ],
  templateUrl: './location-edit.component.html',
  styleUrl: './location-edit.component.scss',
  providers: [
    ViaCepService,
    EmployeeStatusService,
    EmployeeService,
    EmployeeRoleService,
    AddressTypeService,
  ],
  standalone: true,
})
export class LocationEditComponent implements OnInit {
  constructor(
    private formBuilder: FormBuilder,
    private locationService: LocationService,
    private snackbar: SnackbarCustomService,
    private dialogRef: MatDialogRef<LocationEditComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
  ) {
    this.item = this.data.item;
  }

  @Output() responseEdit = new EventEmitter();

  item: any = {};
  form: FormGroup = new FormGroup({});
  loading = false;

  // Seus dados de estados e cidades
  public estadosCidades: EstadoCidade[] = estadosCidades

  public estadosFiltrados$!: Observable<EstadoCidade[]>;
  public cidadesFiltradas$!: Observable<string[]>;
  public cidadesDoEstado: string[] = [];

  ngOnInit(): void {
    this.createForm();
    this.setupAutocompletes();
  }

  close() {
    this.dialogRef.close();
  }

  createModel() {
    // Obtém o objeto de estado completo do FormControl
    const estadoSelecionado = this.form.get('state')?.value;
    // O valor do estado no objeto a ser enviado deve ser a string do estado
    const estadoString = typeof estadoSelecionado === 'string' ? estadoSelecionado : estadoSelecionado?.estado;

    return {
      id: this.item.id,
      descricao: this.form.get('description')?.value,
      cidade: this.form.get('city')?.value,
      estado: estadoString,
      pais: this.form.get('country')?.value,
    };
  }

  createForm(): void {
    // Encontra o objeto de estado completo para inicializar o form
    const estadoInicial = this.estadosCidades.find(e => e.estado === this.item.estado) || this.item.estado;

    this.form = this.formBuilder.group({
      description: [
        this.item.descricao,
        [Validators.required, Validators.minLength(3)],
      ],
      country: [{ value: this.item.pais, disabled: true }, [Validators.required]],
      state: [estadoInicial, [Validators.required]],
      city: [this.item.cidade, [Validators.required, Validators.minLength(3)]],
    });
  }

  setupAutocompletes(): void {
    // Inicializa a lista de cidades com base no estado inicial do item
    const estadoInicial = this.form.get('state')?.value;
    if (estadoInicial && typeof estadoInicial !== 'string') {
      this.cidadesDoEstado = estadoInicial.cidades;
      this.form.get('city')?.enable();
    } else {
      this.form.get('city')?.disable();
    }

    // Lógica para filtrar os estados
    this.estadosFiltrados$ = this.form.get('state')!.valueChanges.pipe(
      startWith(this.form.get('state')?.value),
      map(value => {
        const nomeEstado = typeof value === 'string' ? value : value?.estado;
        return nomeEstado ? this._filterEstados(nomeEstado) : this.estadosCidades.slice();
      })
    );

    // Lógica para quando um estado for selecionado
    this.form.get('state')!.valueChanges.subscribe(value => {
      if (value && typeof value !== 'string') {
        this.form.get('city')?.enable();
        this.cidadesDoEstado = (value as EstadoCidade).cidades;
        // Limpa o campo de cidade apenas se o valor for diferente do valor inicial
        if (this.form.get('city')?.value !== this.item.cidade) {
          this.form.get('city')?.setValue('');
        }
      } else {
        this.form.get('city')?.disable();
        this.cidadesDoEstado = [];
        this.form.get('city')?.setValue('');
      }
    });

    // Lógica para filtrar as cidades
    this.cidadesFiltradas$ = this.form.get('city')!.valueChanges.pipe(
      startWith(this.form.get('city')?.value || ''),
      map(value => this._filterCidades(value || ''))
    );
  }

  editLocation() {
    this.loading = true;
    this.locationService.editLocation(this.createModel()).subscribe({
      next: (response: ResponseRequestModel) => {
        this.snackbar.success(response.message);
        this.responseEdit.emit();
        this.close();
      },
      error: (err: ErrorRequestModel) => {
        this.loading = false;
        this.snackbar.errorRequest(err);
      },
      complete: () => {
        this.loading = false;
      },
    });
  }

  private _filterEstados(value: string): EstadoCidade[] {
    const filterValue = value.toLowerCase();
    return this.estadosCidades.filter(
      estado => estado.estado.toLowerCase().includes(filterValue)
    );
  }

  private _filterCidades(value: string): string[] {
    const filterValue = value.toLowerCase();
    return this.cidadesDoEstado.filter(cidade =>
      cidade.toLowerCase().includes(filterValue)
    );
  }

  public displayEstado(estado: EstadoCidade | null): string {
    return estado ? estado.estado : '';
  }
}
